import {
    computeVideoObjectFitLayout,
    videoPixelToCanvas,
} from "lib/utils/videoObjectFitLayout";

export type MolmoPointOverlay = {
    u: number;
    v: number;
    imageWidth?: number;
    imageHeight?: number;
};

export type DrawMolmoPointOptions = {
    fitScreen: boolean;
    color?: string;
    /** Crosshair arm length in canvas pixels. */
    armLength?: number;
    /** Filled center dot radius in canvas pixels. */
    dotRadius?: number;
    lineWidth?: number;
};

/**
 * Draw Molmo’s inferred (u,v) as a crosshair + dot on the Arm Cam overlay canvas.
 * Coordinates are in the Molmo capture frame; optional imageWidth/Height scale
 * into the live video’s pixel space (same pattern as bbox detections).
 */
export function drawMolmoPointOnCanvas(
    ctx: CanvasRenderingContext2D,
    canvasWidth: number,
    canvasHeight: number,
    videoWidth: number,
    videoHeight: number,
    point: MolmoPointOverlay | null | undefined,
    options: DrawMolmoPointOptions,
): void {
    if (!point || !videoWidth || !videoHeight) return;

    const {
        fitScreen,
        color = "#f59e0b",
        armLength = 14,
        dotRadius = 4,
        lineWidth = 2,
    } = options;

    const layout = computeVideoObjectFitLayout(
        canvasWidth,
        canvasHeight,
        videoWidth,
        videoHeight,
        fitScreen,
    );
    if (!layout) return;

    const dw =
        typeof point.imageWidth === "number" && point.imageWidth > 0
            ? point.imageWidth
            : videoWidth;
    const dh =
        typeof point.imageHeight === "number" && point.imageHeight > 0
            ? point.imageHeight
            : videoHeight;
    const videoU = (point.u * videoWidth) / dw;
    const videoV = (point.v * videoHeight) / dh;
    const canvasPt = videoPixelToCanvas(videoU, videoV, layout);
    if (!canvasPt) return;

    const { x, y } = canvasPt;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = "round";

    ctx.beginPath();
    ctx.moveTo(x - armLength, y);
    ctx.lineTo(x + armLength, y);
    ctx.moveTo(x, y - armLength);
    ctx.lineTo(x, y + armLength);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
}
